<template>
  <section>
    <b-card-actions
      v-if="!isEdit"
      :show-loading="uploading || fetching"
      action-collapse
      :title="$t('Importar arquivo de conciliação')"
    >
      <FormulateForm
        v-show="!uploading"
        ref="form"
      >
        <b-row>
          <b-col md="12">
            <FormulateInput
              id="reconciliation-file"
              ref="conciliationFile"
              v-model="localConciliationFile"
              type="uploader"
              max-files="1"
              class="required"
              :theme="'list'"
              :deletable="!isEdit"
              :meta="true"
              :accept="'.txt'"
              :label="$t('Arquivo Pix')"
              validation="required"
              :help-text="
                $t(
                  'Arraste o arquivo de conciliação Pix aqui (ou clique para selecionar o arquivo)'
                )
              "
              @input="uploadFile"
              @delete="removeFiles"
            />
          </b-col>
        </b-row>
      </FormulateForm>
    </b-card-actions>

    <transition
      v-else
      name="fade"
    >
      <b-row>
        <b-col md="12">
          <b-card-actions
            :show-loading="saving"
            action-collapse
            :title="`${$t('Dados da Conciliação')} #${conciliation.id}`"
            class="h-345"
          >
            <b-row>
              <b-col md="3">
                <FormulateInput
                  id="conciliation-store"
                  :value="conciliation.store"
                  type="label"
                  filter="storeName"
                  :label="$t('Loja')"
                />
              </b-col>
              <b-col md="3">
                <FormulateInput
                  id="conciliation-checking-account"
                  :value="conciliation.checkingAccount.name"
                  type="label"
                  :label="$t('Conta Corrente')"
                />
              </b-col>
              <b-col md="2">
                <FormulateInput
                  id="conciliation-movement-date"
                  :value="conciliation.receivedDate"
                  type="label"
                  filter="date"
                  :label="$t('Data do Movimento')"
                />
              </b-col>
              <b-col md="2">
                <FormulateInput
                  id="invoice-status"
                  :value="$t(`Reconciliation_${conciliation.status}`)"
                  type="label"
                  :label="$t('Status')"
                />
              </b-col>
              <b-col md="2">
                <FormulateInput
                  id="invoice-percent-completed"
                  :value="conciliation.percentCompleted / 100"
                  type="label"
                  filter="percentage"
                  :label="$t('% Concluído')"
                />
              </b-col>
            </b-row>
          </b-card-actions>
        </b-col>

        <b-col md="12">
          <b-tabs fill>
            <b-tab :title="$t('Transações')">
              <conciliation-item-table
                :saving="saving"
                :fetching="fetching"
                @get-data="getData"
                @item-skip="onSkip"
                @item-match="onMatch"
                @item-unmatch="onUnmatch"
                @item-reset="onReset"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </transition>

    <skip-item
      ref="skipModal"
      :conciliation-id="conciliation.id"
    />
    <match-item
      ref="matchModal"
      :conciliation="conciliation"
    />
    <unmatch-item
      ref="unmatchModal"
      :conciliation="conciliation"
    />

    <fab
      v-if="$can('Update', 'SantanderPixConciliation') && !isFinished"
      :main-tooltip="$t('Finalizar Conciliação')"
      main-icon="check"
      :busy="saving"
      @click="onFinishConciliation"
    />
  </section>
</template>

<script>
import { BRow, BCol, BTabs, BTab } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapState } from 'vuex'
import { uploader, reconciliationDomains } from '@/mixins'
import fab from '@/views/components/FAB.vue'
import SkipItem from './components/SkipItem.vue'
import MatchItem from './components/MatchItem.vue'
import UnmatchItem from './components/UnmatchItem.vue'
import ConciliationItemTable from './components/ReconciliationItemTable.vue'

export default {
  components: {
    BRow,
    BCol,
    BTabs,
    BTab,
    BCardActions,
    SkipItem,
    MatchItem,
    UnmatchItem,
    fab,
    ConciliationItemTable,
  },

  mixins: [uploader, reconciliationDomains],

  data() {
    return {
      mode: 'xml',
      uploading: false,
      saving: false,
      fetching: false,
      fetchingAntecipation: false,
    }
  },

  computed: {
    ...mapState('pages/financial/reconciliation/santander/pix/maintain', [
      'conciliation',
      'conciliationFile',
    ]),

    conciliationId() {
      return this.$route.params?.id
    },
    readOnly() {
      return this.conciliation?.status === 'Completed'
    },
    localConciliationFile: {
      get() {
        return this.conciliationFile
      },
      set(val) {
        this.setConciliationFile(val)
      },
    },
    hasFile() {
      return this.conciliationFile.length > 0
    },
    isEdit() {
      return !!this.conciliation?.id
    },
    storeLabel() {
      return this.conciliation?.store?.name
    },
    fileName() {
      return this.conciliationFile?.name
    },
    isFinished() {
      return this.conciliation?.status === this.statusReconciliationEnum.Finished
    },
  },

  created() {
    if (this.conciliationId) this.cleanState()
  },

  async mounted() {
    if (this.conciliationId) {
      try {
        this.fetching = true
        await this.fetchConciliation(this.conciliationId)
        await this.fetchConciliationItems(this.conciliationId)
      } catch (error) {
        this.showError({
          error,
          message: this.$t('Não foi possível obter os dados do Pedido de Compra'),
        })
        this.$router.back()
      } finally {
        this.fetching = false
      }
    }
  },

  methods: {
    ...mapActions('pages/financial/reconciliation/santander/pix', ['fetchManagementAccounts']),
    ...mapActions('pages/financial/reconciliation/santander/pix/maintain', [
      'fetchConciliation',
      'finishConciliation',
      'fetchConciliationItems',
      'setConciliationFile',
      'sendConciliationFile',
      'matchItem',
      'unmatchItem',
      'skipItem',
      'resetItem',
      'cleanState',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchConciliationItems(this.conciliationId)
        } catch (error) {
          this.showError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    async uploadFile() {
      if (this.isEdit || !this.conciliationFile?.length) return
      this.$refs.form.showErrors()
      try {
        this.uploading = true
        const conciliationId = await this.sendConciliationFile({
          file: this.conciliationFile[0],
        })
        this.$router.push({
          name: 'reconciliation-santander-pix-maintain',
          params: { id: conciliationId },
        })
      } catch (error) {
        this.showError({
          error,
          message: this.$t('Não foi possível fazer a leitura do arquivo'),
        })
        this.cleanState()
      } finally {
        this.uploading = false
      }
    },

    async removeFiles() {
      this.cleanState()
      this.conciliationFile = []
    },

    async onFinishConciliation() {
      try {
        if (
          await this.confirm({
            text: this.$t(
              'Confirma a finalização desta conciliação? Os recebíveis associados terão seus pagamentos registrados e a conta corrente será atualizada. Não é possível desfazer esta ação.'
            ),
          })
        ) {
          this.saving = true
          await this.finishConciliation(this.conciliationId)
          this.showSuccess({ message: this.$t('Conciliação finalizada com sucesso.') })
          this.$router.push({ name: 'reconciliation-santander-pix-list' })
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    async onUnmatch(item) {
      if (item.receivableInstallments.length === 1) {
        try {
          if (
            await this.confirm({
              text: this.$t(
                'Confirma a desassociação deste lançamento? Você poderá associá-lo novamente a outro lançamento.'
              ),
            })
          ) {
            this.saving = true
            await this.unmatchItem({
              itemId: item.id,
              conciliationId: this.conciliationId,
              receivableInstallmentIds: item.receivableInstallments.map(r => r.id),
            })
          }
        } catch (error) {
          this.showError({ error })
        } finally {
          this.saving = false
        }
      } else {
        this.$refs.unmatchModal.showModal(item)
      }
    },

    onSkip(item) {
      this.$refs.skipModal.showModal(item)
    },

    onMatch(item) {
      this.$refs.matchModal.showModal(item)
    },

    async onReset(item) {
      try {
        if (
          await this.confirm({
            text: this.$t('Confirma o reset deste lançamento? Ele voltará ao status "Pendente"'),
          })
        ) {
          this.saving = true
          await this.resetItem({ item, conciliationId: this.conciliationId })
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: 0.5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: translateY(-20px);
}
/* .h-345 .card {
  min-height: 345px;
} */
</style>
